import ajax from "@/http/index";
import  { showLoading, hideLoading } from './loading';
import Helper, { queryOptions } from './common';
import { ElMessage, ElMessageBox, FormInstance } from "element-plus";
import { Store, useStore } from "vuex";
import { $t } from "./i18n";

export const paramCache = {
  data: {} as Record<string, any>,
  timestamps: {} as Record<string, number>,
  CACHE_DURATION: 5 * 60 * 1000, // 5分钟缓存
  
  isCacheValid(key: string): boolean {
    const timestamp = this.timestamps[key];
    if (!timestamp) return false;
    return Date.now() - timestamp < this.CACHE_DURATION;
  },
  
  get(key: string): any | null {
    if (this.isCacheValid(key)) {
      return this.data[key];
    }
    return null;
  },
  
  set(key: string, data: any): void {
    this.data[key] = data;
    this.timestamps[key] = Date.now();
  },
  
  invalidate(key: string): void {
    delete this.timestamps[key];
  },
  
  invalidateAll(): void {
    this.timestamps = {};
  }
};

export class DbHelper {
  /**
   * 获取 URL 的统一方法
   * 优先使用 option.url (Vue 3 / 自定义)，否则使用 Helper.getUrl (Vue 2)
   */
  private static getUrl(option: queryOptions): string {
    return Helper.getVal(option.url) ? option.url ?? '' : Helper.getUrl(option.type);
  }

  /**
   * 处理 Loading 状态的统一方法
   */
  private static handleLoading(option: queryOptions, isLoading: boolean) {
    // 如果 loading 是对象 { load: uiData.loading, key: 'datalist' }
    if (option.loading && typeof option.loading === 'object' && !Array.isArray(option.loading)) {
        if(option.loading.load && option.loading.key){
            const loadObj = option.loading.load;
            const key = option.loading.key;
            
            if(Helper.getVal(loadObj[key]) != null){
                loadObj[key] = isLoading;
            } else {
                // 属性不存在，使用 Vue.set 或 Object.assign 确保响应式
                // 在 Vue 3 中，reactive 对象可以直接添加新属性，但为了确保触发更新，我们可以使用以下方法：
                Object.assign(loadObj, { [key]: isLoading });
            }
            return;
        }
        option.loading = isLoading;
    }

    // 如果 loading 是布尔值，使用全局 Loading
    if (typeof option.loading === 'boolean') {
        option.loading = isLoading;
        isLoading ? showLoading() : hideLoading();
        return;
    }
  }

  /**
   * 处理表单验证的统一方法
   * 支持 Promise (Vue 3) 和 Callback (Vue 2)
   */
  private static validateForm(option: queryOptions): Promise<any> {
    if (!option.form) return Promise.resolve();

    const frm: FormInstance = option.form;

    // 如果指定了 fields，使用 validateField (Callback 模式)
    if (Helper.getVal(option.fields)) {
      return new Promise((resolve, reject) => {
        frm.validateField(option.fields ?? [], (isValid) => {
          isValid ? resolve(true) : reject(option.fields);
        });
      });
    }

    // 否则使用全表验证 (Promise 模式)
    return frm.validate();
  }

  static all(options: queryOptions | any[]): Promise<any> {
    let _obj: any = {};
    (options as any[]).forEach((item: any) => {
      _obj[item.key] = DbHelper.get(item);
    });

    return ajax.all(_obj).then((responses: any) => {
      const result: any = {};
      Object.keys(responses).forEach(key => {
        result[key] = responses[key].data.map ?? [];
      });
      return result;
    });
  }

  static set(option: queryOptions, config: any = null) {
    // 1. 验证表单
    return DbHelper.validateForm(option)
      .then(() => DbHelper.post(option, config)); // 2. 验证通过后执行 post
  }

  static delete(option: queryOptions, config: any = null) {
    return new Promise((resolve, reject) => {
      if (!option.multiples || option.multiples.length === 0) {
        ElMessage.error({message: '没有项目被选择', grouping:true});
        return reject('multiples error: no items selected');
      }

      const doRequest = () => {
        DbHelper.post(option, config)
          .then(resolve)
          .catch(reject);
      };

      if (option.hasAsk) {
        ElMessageBox.confirm($t('sys.DELETE_ASK'), $t('sys.PROPMPT'), {
          confirmButtonText: $t('sys.OK'),
          cancelButtonText: $t('sys.CANCEL'),
          type: 'warning',
        })
          .then(doRequest)
          .catch((err) => {
            // 区分用户点击取消和真正的错误
            reject({ msg: 'cancel', data: err });
          });
      } else {
        doRequest();
      }
    });
  }

  static download(option: any) {
    const url = DbHelper.getUrl(option);

    return new Promise((resolve, reject) => {
      ajax.get(url, {
        params: option.params,
        responseType: 'blob',
        onDownloadProgress: option.onDownloadProgress
      }).then((response: any) => {

        // ==========================================
        // 核心修改：尝试从响应头获取后端定义的文件名
        // ==========================================
        let filename = option.filename; // 默认使用前端传来的文件名
        
        // 获取响应头 content-disposition
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          // 正则匹配 filename="xxx" 或 filename=xxx
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) { 
            filename = matches[1].replace(/['"]/g, ''); // 去掉可能的引号
            // 处理后端可能进行的 URL 编码 (如中文文件名)
            filename = decodeURIComponent(filename); 
          }
        }
        // ==========================================

        let tempUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = tempUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        // 【修复1】：清理 DOM 节点
        document.body.removeChild(link); 
        // 【修复2】：释放 Blob URL，防止内存泄漏
        window.URL.revokeObjectURL(tempUrl); 
        
        resolve(true); 
      }).catch((err: any) => {
        // 【修复3】：如果是后端报错，尝试读取 blob 中的错误信息，否则直接提示
        console.error('下载失败', err);
        reject(err);
      });
    });
  }

  static get(option: queryOptions, config: any = null) {
    const url = DbHelper.getUrl(option);
    
    // 1. 设置默认 headers
    if (Helper.getVal(option.headers) == null) {
      option.headers = {};
    }

    // 2. 开启 Loading
    DbHelper.handleLoading(option, true);

    return new Promise((resolve, reject) => {
      ajax.get(url, { ...config, params: option.params, headers: option.headers })
        .then((res: any) => {
          resolve(res);
        })
        .catch((err: any) => {
          // 只有非取消的错误才向外抛出
          if (err?.code !== 'ERR_CANCELED') {
            reject(err);
          } else {
            // 如果是取消请求，依然 reject 以便 Promise 链结束，但可以在这里做特殊处理
            reject(err); 
          }
        })
        .finally(() => {
          // 3. 关闭 Loading
          DbHelper.handleLoading(option, false);
        });
    });
  }

  private static post(option: queryOptions, config: any = null) {
    const url = DbHelper.getUrl(option);

    // 1. 开启 Loading
    DbHelper.handleLoading(option, true);

    return new Promise((resolve, reject) => {
      ajax.post(url, option.params, config)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          // 2. 关闭 Loading
          DbHelper.handleLoading(option, false);
        });
    });
  }
}


export class vueHelper {
  private static config:any = {};

  static form_OnSearch(e:any, options:any, callback:any=null){
    if(Helper.getVal(e) == null) {
      if(callback!=null) callback([]);
      return;
    }

    let _type = '';
    let _mType = '';
    switch(options.t){
      case 'dispatchers':
      case 'staffs':
        _type = 'staff'; 
        break;
      case 'drivers':
        _type = 'driver';
        break;
      case 'cars':
        _type = 'car';
        break;
      case 'clients':
      case 'suppliers':
      case 'enterprises':
        _type = 'enterprise';
        break;
      case 'members':
      case 'payers':
      case 'receives':
        _type = 'member';
        break;
    }

    Helper.exec(_type, 'get', {
        loading: options.loading,
        params: { 'type': _mType, 'fields': '*', 'data' : JSON.stringify({'search': e})}
    }).then((res:any)=>{
      console.log(res.data);
      if(callback!=null) callback(res.data.map);
    }).catch((err:any)=>{
      console.log(err);
    });
  }

  static rights_OnInit(conf:any, callback:any=null){
    let store:any = useStore();
    vueHelper.config = conf;

    if(Helper.getVal(store.state.manage) == null){
      Helper.Debug(null, '用户未登录或本页功能无须登录');
      if(callback!=null) callback(null); //{code: 'nologin'}
      return;
    }

    let isReLoadRights:boolean = true; //是否需要重新加截
    if(store.state.manage.expiredate == '' || Math.round(Math.abs((new Date().getTime() - Number(store.state.manage.expiredate)) / 7 * 24 * 3600 * 1000)) > 1){
      store.dispatch('manage/USER_EXPIRE', new Date());
        isReLoadRights = true;
    }
    
    if(isReLoadRights || Helper.getVal(conf.rights) == null) {
      //重新加载
      vueHelper.rights_init({
        stateRights: store.state.manage.rights,
        rights: conf.rights,
        rightskey: conf.rightskey
      }, (data:any)=>{
        if(!Helper.isJson(data)){
          conf.router.replace(data);
          return;
        }

        conf = {
          rightskey: conf.rightskey,
          rights: JSON.parse(JSON.stringify(data))
        };

        vueHelper.config = conf;

        if(!vueHelper.checkright('browse', { store: store }) && !vueHelper.checkright('all-browse', { store: store})){
          Helper.Debug(vueHelper.config, '==== NO BROWSE =====');
          return;
        }

        Helper.Debug(vueHelper.config.rights, 'rights');

        if(callback!=null){
          callback(vueHelper.config);
        }
      });
    }
  }

  static checkright(rightkey: string, options:any={}):boolean{

    if(Helper.getVal(options.store.state.manage) == null){
        return false;
    }

    console.log(vueHelper.config);

    return vueHelper.right_OnCheck({
        stateRights: options.store.state.manage.rights,
        rights: vueHelper.config.rights,
        rightskey: vueHelper.config.rightskey,
        handler: rightkey
    });
  }

  private static right_OnCheck(option:any){
    if(option.stateRights == 'system-all') return true;

    let _handler:string = option.handler.replace(/\-/g, '_');

    if(option.stateRights.replace(/\-/g, '_').indexOf(_handler)>-1){
      if(_handler.split('_').length - 1 > 1){
        return option.rights[option.handler];
      }else{
        return option.rights[option.rightskey + '_' + _handler];
      }
    }

    return false;
  }

  private static async rights_init(option:any, callback:any=null){
    let _rights:any = {};

    await Helper.exec('rights', 'get', {
      params: { "key": option.rightskey }
    }).then((res:any)=>{
      res.data.map.forEach((item: any) => {
        _rights[item.menukey + "_" + item.code] = false;
      });

      if (option.stateRights == 'system-all'){
        Object.getOwnPropertyNames(_rights).forEach(key=>{
          _rights[key] = true;
        });

        if(callback!=null) callback(_rights);
        return;
      }

      Helper.changeRights(option.stateRights, _rights);

      if (!_rights[option.rightskey.split(",")[0] + "_browse"]){
        _rights = [];
      }

      if(callback!=null) callback(_rights);
    }).catch((err:any)=>{
      console.log(err);
    });
  }
}

export default DbHelper